// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { useCompletionConfirmation } from './use-completion-confirmation';
import { ScreenLayout } from '@shared/advisory/account-workflow/layout';
import { Translation } from '@shared/ui-kit/providers';
import { AppIcon } from '@shared/ui-kit/components';
import { Button, Alert, AlertTitle, Typography } from '@mui/material';

export const CompletionConfirmation = defineComponent({
  displayName: 'CompletionConfirmation',
  useSetup: useCompletionConfirmation,
  View: ({ onNext, advisoryTypeName }) => {
    return (
      <ScreenLayout
        actions={
          <div className="flex shrink-0 items-center justify-center border-t p-8">
            <Button
              color="secondary"
              data-testid="complete-button"
              size="large"
              startIcon={<AppIcon icon="Home" size="lg" />}
              type="button"
              onClick={onNext}
            >
              <Translation
                i18nKey="onboarding-process-overview.confirmation.back-to-home-button"
                ns="components"
              />
            </Button>
          </div>
        }
      >
        <div className="mx-auto flex max-w-3xl flex-col items-center space-y-6">
          <div>
            <AppIcon icon="CheckCircle" size={56} color="success" />
          </div>

          <Typography
            variant="h3"
            className="text-center"
            data-testid="confirmation-title"
          >
            <Translation
              i18nKey="onboarding-process-overview.confirmation.title"
              ns="components"
            />
          </Typography>

          <div className="w-full space-y-4 text-left">
            <Translation
              i18nKey="onboarding-process-overview.confirmation.body"
              ns="components"
              components={{
                p: <Typography variant="body1" component="p" />,
                small: <Typography variant="body2" component="p" />,
              }}
              values={{ advisoryType: advisoryTypeName.toLowerCase() }}
            />
          </div>

          <Alert severity="info" className="w-full !rounded-lg" icon={false}>
            <AlertTitle>
              <Translation
                i18nKey="onboarding-process-overview.confirmation.help.title"
                ns="components"
              />
            </AlertTitle>

            <Typography variant="body2" className="mb-4">
              <Translation
                i18nKey="onboarding-process-overview.confirmation.help.description"
                ns="components"
              />
            </Typography>

            <ul className="space-y-2">
              <li>
                <Typography variant="body2" component="span">
                  <Translation
                    i18nKey="onboarding-process-overview.confirmation.help.email"
                    ns="components"
                  />
                </Typography>
              </li>
              <li>
                <Typography variant="body2" component="span">
                  <Translation
                    i18nKey="onboarding-process-overview.confirmation.help.phone"
                    ns="components"
                  />
                </Typography>
              </li>
              <li>
                <Typography variant="body2" component="span">
                  <Translation
                    i18nKey="onboarding-process-overview.confirmation.help.chat"
                    ns="components"
                  />
                </Typography>
              </li>
            </ul>
          </Alert>
        </div>
      </ScreenLayout>
    );
  },
});
